import { combineLatest, map, of, switchMap } from 'rxjs';
import { dispatchAction } from '@ardoq/rxbeach';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { permissionsOperations } from '@ardoq/access-control';
import { emitOverviewActionEvent } from 'integrations/common/tracking/utils';
import {
  connectionClick,
  listConnections,
  updateConnectionStatuses,
  upsertConnectionInitiate,
} from 'integrations/common/streams/connections/actions';
import { getConnectionsStream } from 'integrations/common/streams/connections/connections$';
import type { Connection } from '@ardoq/api-types/integrations';
import { startDeleteConnectionModal } from 'integrations/common/modals/deleteConnectionModal/DeleteConnectionModal';
import { integrationId$ } from 'integrations/common/streams/integrationId/integrationId$';
import type { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { getIntegrationTermsDictionaryStream } from 'integrations/common/streams/integrationTermsDictionary/getIntegrationTermsDictionaryStream';
import { getIntegrationExtraColumns } from './columns';
import { sortBy } from 'lodash/fp';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t } from '@ardoq/i18n';

const onDelete = (integrationId: IntegrationId, connection: Connection) => {
  emitOverviewActionEvent('connection-delete', integrationId);
  startDeleteConnectionModal(integrationId, connection?._id).then(() => {
    dispatchAction(
      updateConnectionStatuses({
        integrationId: integrationId,
        statuses: { delete: { status: 'INIT' } },
      })
    );
  });
};

const onUpsertClick = (
  integrationId: IntegrationId,
  connection?: Connection
) => {
  emitOverviewActionEvent(
    connection ? 'connection-edit' : 'connection-create',
    integrationId
  );
  dispatchAction(upsertConnectionInitiate({ integrationId, connection }));
};

const onConnectionClick = (
  integrationId: IntegrationId,
  connection: Connection
) => {
  dispatchAction(connectionClick({ integrationId: integrationId, connection }));
};

const onRefresh = (integrationId: IntegrationId) => {
  dispatchAction(listConnections({ integrationId }));
};

export const viewModel$ = integrationId$.pipe(
  switchMap(integrationId => {
    return combineLatest([
      currentUserPermissionContext$,
      getConnectionsStream(integrationId),
      getIntegrationTermsDictionaryStream(integrationId),
      of(integrationId),
      i18nLocale$,
    ]).pipe(
      map(
        ([
          permissionContext,
          { connections, statuses },
          { connectYourAccountTitle, connectYourAccountBtn },
          integrationId,
          i18nLocale,
        ]) => ({
          connections: sortBy('name', connections),
          statuses,
          isAdmin: permissionsOperations.hasAdminRole(permissionContext),
          onDelete: (connection: Connection) =>
            onDelete(integrationId, connection),
          onUpsertClick: (connection?: Connection) =>
            onUpsertClick(integrationId, connection),
          onRefresh: () => onRefresh(integrationId),
          onConnectionClick: (connection: Connection) =>
            onConnectionClick(integrationId, connection),
          extraColumns: getIntegrationExtraColumns(integrationId, i18nLocale),
          connectYourAccountTitle,
          connectYourAccountBtn,
          refreshButtonLabel: t(
            i18nLocale,
            'integrations:connections.refreshApp',
            'Refresh app'
          ),
          createNewConnectionLabel: t(
            i18nLocale,
            'integrations:connections.createNewConnection',
            'Create new connection'
          ),
          i18nLocale,
        })
      )
    );
  })
);
